/**
 * MinaRoMina Product JS
 * Handles product variant selection, media gallery, and AJAX cart functionality
 */

class MinaRominaProduct {
  constructor() {
    this.productForm = document.querySelector('[data-product-form]');
    this.productMedia = document.querySelector('[data-product-media]');
    this.buyButtons = document.querySelector('[data-buy-buttons]');
    this.productRich = document.querySelector('[data-product-rich]');

    // Define translations for both languages
    this.translations = {
      'es': {
        addToCart: 'AGREGAR AL CARRITO',
        soldOut: 'AGOTADO',
        quantity: 'Cantidad:',
        availableNow: 'Disponible ahora',
        currentlyUnavailable: 'Actualmente no disponible',
        inStock: 'en stock'
      },
      'en': {
        addToCart: 'ADD TO CART',
        soldOut: 'SOLD OUT',
        quantity: 'Quantity:',
        availableNow: 'Available now',
        currentlyUnavailable: 'Currently unavailable',
        inStock: 'in stock'
      }
    };

    // Detect current language
    this.currentLang = this.detectLanguage();

    if (this.productForm) {
      this.init();
    }
  }

  // Detect current language from document or domain
  detectLanguage() {
    const docLang = document.documentElement.lang || 'es';
    const hostname = window.location.hostname;
    const isUS = hostname.includes('.us');
    const detectedLang = (docLang.startsWith('en') || isUS) ? 'en' : 'es';

    console.log('🌐 Language Detection:', {
      docLang,
      hostname,
      isUS,
      detectedLang
    });

    return detectedLang;
  }

  // Get translation for current language
  t(key) {
    return this.translations[this.currentLang][key] || this.translations['es'][key];
  }

  init() {
    this.initVariantSelection();
    this.initMediaGallery();
    this.initQuantityControls();
    this.initAddToCart();
    this.initProductRich();
    this.initZoomModal();
    // Translations removed - handled by Liquid templates
  }

  // Watch for DOM changes that might revert translations
  initTranslationObserver() {
    // Store observer reference
    this.translationObserver = null;
    this.isApplyingTranslations = false;

    // Observe the buy buttons container for changes
    if (this.buyButtons) {
      this.translationObserver = new MutationObserver(() => {
        // Skip if we're already applying translations
        if (this.isApplyingTranslations) return;
        // Use microtask to ensure we run after all DOM updates
        queueMicrotask(() => this.applyTranslations());
      });

      this.translationObserver.observe(this.buyButtons, {
        childList: true,
        subtree: true,
        characterData: true,
        characterDataOldValue: true,
        attributes: true,
        attributeOldValue: true
      });
    }

    // Also listen for Shopify section events
    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => this.forceSpanishTranslations(), 100);
    });

    // Listen to our own custom event
    document.addEventListener('mm:variant:change', () => {
      queueMicrotask(() => this.forceSpanishTranslations());
    });
  }

  // Variant Selection
  initVariantSelection() {
    const variantSelectors = this.productForm.querySelectorAll('[data-option-input], [data-option-select]');

    variantSelectors.forEach(selector => {
      selector.addEventListener('change', () => {
        this.handleVariantChange();
      });
    });
  }

  handleVariantChange() {
    const formData = new FormData(this.productForm);
    const options = {};

    for (let [key, value] of formData.entries()) {
      if (key.startsWith('options[')) {
        const optionName = key.match(/options\[(.*?)\]/)[1];
        options[optionName] = value;
      }
    }

    // Find matching variant
    const variants = this.getProductVariants();
    const selectedVariant = this.findVariantByOptions(variants, options);

    if (selectedVariant) {
      this.updateVariantInfo(selectedVariant);
      this.updateVariantMedia(selectedVariant);
      this.updateVariantUrl(selectedVariant);
    }
  }

  getProductVariants() {
    const productJson = document.querySelector('#ProductJSON-' + this.productForm.dataset.productId);
    return productJson ? JSON.parse(productJson.textContent).variants : [];
  }

  findVariantByOptions(variants, options) {
    return variants.find(variant => {
      return variant.options.every((option, index) => {
        const optionName = Object.keys(options)[index];
        return options[optionName] === option;
      });
    });
  }

  updateVariantInfo(variant) {
    // Update hidden variant ID input
    const variantIdInput = this.productForm.querySelector('[data-variant-id]');
    if (variantIdInput) {
      variantIdInput.value = variant.id;
      variantIdInput.disabled = !variant.available;
    }

    // Update price display
    const priceContainer = document.querySelector('[data-price-container]');
    if (priceContainer) {
      const currentPrice = priceContainer.querySelector('[data-current-price]');
      const comparePrice = priceContainer.querySelector('[data-compare-price]');

      if (currentPrice) {
        currentPrice.textContent = this.formatMoney(variant.price);
        currentPrice.classList.toggle('mm-product-info__price-current--sale', variant.compare_at_price > variant.price);
      }

      if (comparePrice) {
        if (variant.compare_at_price > variant.price) {
          comparePrice.textContent = this.formatMoney(variant.compare_at_price);
          comparePrice.style.display = '';
        } else {
          comparePrice.style.display = 'none';
        }
      }
    }

    // Update SKU
    const skuElement = document.querySelector('[data-variant-sku]');
    if (skuElement) {
      skuElement.textContent = variant.sku || '';
    }

    // Update availability
    const availabilityElement = document.querySelector('[data-availability]');
    if (availabilityElement) {
      const availabilityText = availabilityElement.querySelector('.mm-product-info__availability-text');
      if (availabilityText) {
        if (variant.available) {
          if (variant.inventory_quantity > 0 && variant.inventory_quantity <= 10) {
            availabilityText.textContent = `${variant.inventory_quantity} en stock`;
          } else {
            availabilityText.textContent = 'Disponible ahora';
          }
        } else {
          availabilityText.textContent = 'Actualmente no disponible';
        }
      }
    }

    // Update add to cart button
    const addToCartButton = this.productForm.querySelector('[data-add-to-cart]');
    if (addToCartButton) {
      const addText = addToCartButton.querySelector('.mm-buy-buttons__add-text');
      addToCartButton.disabled = !variant.available;

      // Hardcoded language detection - simple and safe
      if (addText) {
        const isEnglish = document.documentElement.lang.startsWith('en') || window.location.hostname.includes('.us');
        if (isEnglish) {
          addText.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
        } else {
          addText.textContent = variant.available ? 'AGREGAR AL CARRITO' : 'AGOTADO';
        }
      }
    }

    // Enhanced aria-live announcement for variant changes
    if (window.MinaRominaA11y) {
      const priceText = this.formatMoney(variant.price);
      const availabilityText = variant.available ? 'available' : 'currently unavailable';
      window.MinaRominaA11y.announce(`Variant selected. Price: ${priceText}, ${availabilityText}`, 'polite');
    }

    // Dispatch custom event with detailed info
    const productId = this.productForm.dataset.productId;
    const eventDetail = {
      productId: productId,
      variantId: variant.id,
      price: variant.price,
      available: variant.available,
      variant: variant
    };

    this.dispatchEvent('mm:variant:change', eventDetail);

    // Also dispatch on document for gallery sync
    document.dispatchEvent(new CustomEvent('variant:changed', {
      detail: eventDetail,
      bubbles: true
    }));

    // Translations handled by Liquid - no JS intervention needed
  }

  updateVariantMedia(variant) {
    if (!variant.featured_media) return;

    // Try to sync with Swiper gallery
    if (window.productGallerySwiper) {
      const slides = document.querySelectorAll('.swiper-slide[data-media-id]');
      let targetIndex = -1;

      slides.forEach((slide, index) => {
        if (slide.dataset.mediaId == variant.featured_media.id) {
          targetIndex = index;
        }
      });

      if (targetIndex >= 0) {
        window.productGallerySwiper.slideToLoop(targetIndex, 300);
        console.log('[Product] Gallery synced to media:', variant.featured_media.id);
        return;
      }
    }

    // Fallback to old method
    if (!this.productMedia) return;
    const targetMedia = this.productMedia.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
    if (targetMedia) {
      this.setActiveMedia(variant.featured_media.id);
    }
  }

  updateVariantUrl(variant) {
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  // Media Gallery
  initMediaGallery() {
    if (!this.productMedia) return;

    const thumbs = this.productMedia.querySelectorAll('[data-media-thumb]');
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        const mediaId = thumb.dataset.mediaThumb;
        this.setActiveMedia(mediaId);
      });

      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const mediaId = thumb.dataset.mediaThumb;
          this.setActiveMedia(mediaId);
        }
      });
    });

    // Keyboard navigation
    this.initMediaKeyboardNav();
  }

  setActiveMedia(mediaId) {
    if (!this.productMedia) return;

    // Update main media
    const allMediaItems = this.productMedia.querySelectorAll('[data-media-id]');
    allMediaItems.forEach(item => {
      item.hidden = item.dataset.mediaId !== mediaId;
      item.classList.toggle('mm-product-media__item--active', item.dataset.mediaId === mediaId);
    });

    // Update thumbnails
    const allThumbs = this.productMedia.querySelectorAll('[data-media-thumb]');
    allThumbs.forEach(thumb => {
      const isActive = thumb.dataset.mediaThumb === mediaId;
      thumb.classList.toggle('mm-product-media__thumb--active', isActive);
      thumb.setAttribute('aria-selected', isActive);
    });
  }

  initMediaKeyboardNav() {
    const thumbContainer = this.productMedia.querySelector('[data-media-thumbs]');
    if (!thumbContainer) return;

    thumbContainer.addEventListener('keydown', (e) => {
      const thumbs = [...thumbContainer.querySelectorAll('[data-media-thumb]')];
      const currentIndex = thumbs.findIndex(thumb => thumb === document.activeElement);

      let newIndex = currentIndex;

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          newIndex = currentIndex > 0 ? currentIndex - 1 : thumbs.length - 1;
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          newIndex = currentIndex < thumbs.length - 1 ? currentIndex + 1 : 0;
          break;
        case 'Home':
          e.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          newIndex = thumbs.length - 1;
          break;
      }

      if (newIndex !== currentIndex && thumbs[newIndex]) {
        thumbs[newIndex].focus();
        thumbs[newIndex].click();
      }
    });
  }

  // Quantity Controls
  initQuantityControls() {
    if (!this.buyButtons) return;

    const quantityInput = this.buyButtons.querySelector('[data-quantity-input]');
    const decreaseBtn = this.buyButtons.querySelector('[data-quantity-decrease]');
    const increaseBtn = this.buyButtons.querySelector('[data-quantity-increase]');

    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', () => {
        this.updateQuantity(-1);
      });
    }

    if (increaseBtn) {
      increaseBtn.addEventListener('click', () => {
        this.updateQuantity(1);
      });
    }

    if (quantityInput) {
      quantityInput.addEventListener('change', () => {
        this.validateQuantity();
      });
    }
  }

  updateQuantity(change) {
    const quantityInput = this.buyButtons.querySelector('[data-quantity-input]');
    if (!quantityInput) return;

    const currentValue = parseInt(quantityInput.value) || 1;
    const newValue = Math.max(1, currentValue + change);

    quantityInput.value = newValue;
    this.validateQuantity();
  }

  validateQuantity() {
    const quantityInput = this.buyButtons.querySelector('[data-quantity-input]');
    if (!quantityInput) return;

    const value = parseInt(quantityInput.value);
    const min = parseInt(quantityInput.min) || 1;

    if (isNaN(value) || value < min) {
      quantityInput.value = min;
    }

    // Update button states
    const decreaseBtn = this.buyButtons.querySelector('[data-quantity-decrease]');
    if (decreaseBtn) {
      decreaseBtn.disabled = parseInt(quantityInput.value) <= min;
    }
  }

  // Add to Cart
  initAddToCart() {
    if (!this.productForm) return;

    this.productForm.addEventListener('submit', (e) => {
      e.preventDefault();
      this.addToCart();
    });
  }

  async addToCart(retryCount = 0) {
    const formData = new FormData(this.productForm);
    const addToCartButton = this.productForm.querySelector('[data-add-to-cart]');

    if (!addToCartButton) return;

    // Show loading state
    this.setLoadingState(addToCartButton, true);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const item = await response.json();
        this.handleAddToCartSuccess(item);
      } else {
        const error = await response.json();
        this.handleAddToCartError(error, retryCount);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        this.handleAddToCartError({
          message: 'Request timed out',
          code: 'TIMEOUT'
        }, retryCount);
      } else if (error.name === 'TypeError' && retryCount < 1) {
        // Network error - retry once
        if (window.MinaRominaA11y) {
          window.MinaRominaA11y.announce('Connection issue, retrying...', 'polite');
        }
        setTimeout(() => this.addToCart(retryCount + 1), 1000);
        return;
      } else {
        this.handleAddToCartError({
          message: 'Network error occurred',
          code: 'NETWORK_ERROR'
        }, retryCount);
      }
    } finally {
      this.setLoadingState(addToCartButton, false);
    }
  }

  setLoadingState(button, loading) {
    const loadingElement = button.querySelector('.mm-buy-buttons__loading');
    const textElement = button.querySelector('.mm-buy-buttons__add-text');

    button.disabled = loading;

    if (loadingElement) {
      loadingElement.hidden = !loading;
    }

    if (textElement) {
      textElement.style.opacity = loading ? '0' : '1';
    }
  }

  async handleAddToCartSuccess(item) {
    console.log('✅ [Product] handleAddToCartSuccess called:', item);

    // Show success message
    this.showMessage('Product added to cart!', 'success');

    // Enhanced aria-live announcement
    if (window.MinaRominaA11y) {
      window.MinaRominaA11y.announce(`${item.product_title} has been added to your cart`, 'polite');
    }

    // Get updated cart data
    try {
      console.log('🔄 [Product] Fetching cart data...');
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      console.log('📦 [Product] Cart data fetched:', cart);

      // Enhanced aria-live announcement with cart count
      if (window.MinaRominaA11y) {
        const itemText = cart.item_count === 1 ? 'item' : 'items';
        window.MinaRominaA11y.announce(`Cart updated. You now have ${cart.item_count} ${itemText} in your cart`, 'polite');
      }

      // Dispatch cart update event with full cart data
      console.log('🚀 [Product] Dispatching mm:cart:update event...');
      this.dispatchEvent('mm:cart:update', {
        item_count: cart.item_count,
        total_price: cart.total_price,
        items: cart.items,
        addedItem: item
      });
      console.log('✅ [Product] Event dispatched successfully');
    } catch (error) {
      console.error('❌ [Product] Error fetching cart:', error);
      // Fallback event with just the item
      this.dispatchEvent('mm:cart:add', { item });
    }

    // Update cart count if element exists
    this.updateCartCount();
  }

  handleAddToCartError(error, retryCount = 0) {
    const message = error.message || error.description || 'Failed to add to cart';

    // Enhanced error messaging with retry option
    if (error.code === 'NETWORK_ERROR' && retryCount < 1) {
      this.showMessage('Connection issue. Click to retry.', 'error', {
        action: 'Retry',
        callback: () => this.addToCart(retryCount + 1)
      });
    } else if (error.code === 'TIMEOUT') {
      this.showMessage('Request timed out. Please try again.', 'error');
    } else {
      this.showMessage(message, 'error');
    }

    // Enhanced aria-live announcement
    if (window.MinaRominaA11y) {
      window.MinaRominaA11y.announce(`Error adding to cart: ${message}`, 'assertive');
    }

    // Dispatch error event for other components to handle
    this.dispatchEvent('mm:cart:error', { error, retryCount });

    // Show in form errors container
    const errorsContainer = this.productForm.querySelector('[data-form-errors]');
    if (errorsContainer) {
      errorsContainer.textContent = message;
      errorsContainer.hidden = false;
      errorsContainer.focus();
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
      });
    } catch (error) {
      // Silent fail
    }
  }

  // Product Rich Content (Tabs/Accordion)
  initProductRich() {
    if (!this.productRich) return;

    const format = this.productRich.dataset.format;

    if (format === 'tabs') {
      this.initTabs();
    } else {
      this.initAccordion();
    }
  }

  initTabs() {
    const tabs = this.productRich.querySelectorAll('[data-tab]');

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabIndex = tab.dataset.tab;
        this.setActiveTab(tabIndex);
      });
    });
  }

  setActiveTab(tabIndex) {
    const tabs = this.productRich.querySelectorAll('[data-tab]');
    const panels = this.productRich.querySelectorAll('[data-panel]');

    tabs.forEach((tab, index) => {
      const isActive = index.toString() === tabIndex;
      tab.classList.toggle('mm-product-rich__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive);
    });

    panels.forEach((panel, index) => {
      const isActive = index.toString() === tabIndex;
      panel.classList.toggle('mm-product-rich__panel--active', isActive);
      panel.hidden = !isActive;
    });
  }

  initAccordion() {
    const triggers = this.productRich.querySelectorAll('[data-accordion-trigger]');

    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        const content = trigger.nextElementSibling;

        trigger.setAttribute('aria-expanded', !isExpanded);

        if (content) {
          content.classList.toggle('mm-product-rich__panel-content--collapsed', isExpanded);
          content.classList.toggle('mm-product-rich__panel-content--expanded', !isExpanded);
        }
      });
    });
  }

  // Zoom Modal
  initZoomModal() {
    const zoomModal = document.querySelector('[data-zoom-modal]');
    if (!zoomModal) return;

    const zoomableImages = this.productMedia.querySelectorAll('.mm-product-media__image-wrapper--zoomable');

    zoomableImages.forEach(wrapper => {
      wrapper.addEventListener('click', (e) => {
        const img = wrapper.querySelector('img');
        if (img) {
          this.openZoomModal(img);
        }
      });
    });

    // Close modal handlers
    const closeButtons = zoomModal.querySelectorAll('[data-zoom-close]');
    closeButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.closeZoomModal();
      });
    });

    // Escape key handler
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !zoomModal.hidden) {
        this.closeZoomModal();
      }
    });
  }

  openZoomModal(img) {
    const zoomModal = document.querySelector('[data-zoom-modal]');
    const zoomImage = zoomModal.querySelector('[data-zoom-image]');

    if (zoomImage && img.dataset.zoomSrc) {
      zoomImage.src = img.dataset.zoomSrc;
      zoomImage.alt = img.alt;
    }

    // Use global a11y utility for proper focus management
    if (window.MinaRominaA11y) {
      window.MinaRominaA11y.openDialog(zoomModal, {
        invoker: img,
        closeOnOverlayClick: true,
        preventBodyScroll: true
      });
    } else {
      // Fallback if a11y util not available
      zoomModal.hidden = false;
      document.body.style.overflow = 'hidden';
      const closeButton = zoomModal.querySelector('[data-zoom-close]');
      if (closeButton) closeButton.focus();
    }
  }

  closeZoomModal() {
    const zoomModal = document.querySelector('[data-zoom-modal]');

    // Use global a11y utility for proper focus restoration
    if (window.MinaRominaA11y) {
      window.MinaRominaA11y.closeDialog(zoomModal, { restoreBodyScroll: true });
    } else {
      // Fallback if a11y util not available
      zoomModal.hidden = true;
      document.body.style.overflow = '';
    }
  }

  // Utilities
  formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }

  // Apply translations after any DOM update
  applyTranslations() {
    // Prevent re-entry
    if (this.isApplyingTranslations) {
      console.log('⏸️ Already applying translations, skipping...');
      return;
    }

    this.isApplyingTranslations = true;
    console.log('🔄 Applying translations, current lang:', this.currentLang);

    try {
      // Fix button text based on current language
      const addToCartButtons = document.querySelectorAll('[data-add-to-cart] .mm-buy-buttons__add-text, button[name="add"] .mm-buy-buttons__add-text');
      console.log('🔍 Found buttons:', addToCartButtons.length);

      addToCartButtons.forEach((el) => {
        const currentText = el.textContent.trim();
        const text = currentText.toLowerCase();

        // Only change if the text is different from the expected translation
        if (text.includes('add') || text.includes('agregar')) {
          const expectedText = this.t('addToCart');
          if (currentText !== expectedText) {
            el.textContent = expectedText;
            console.log('✅ Changed to addToCart:', el.textContent);
          }
        }
        else if (text.includes('sold') || text.includes('out') || text.includes('agotado')) {
          const expectedText = this.t('soldOut');
          if (currentText !== expectedText) {
            el.textContent = expectedText;
            console.log('✅ Changed to soldOut:', el.textContent);
          }
        }
      });

      // Fix quantity label
      const qtyLabels = document.querySelectorAll('.mm-buy-buttons__quantity-label, .product-form__quantity-label');
      qtyLabels.forEach((el) => {
        if (/quantity:?/i.test(el.textContent) || /cantidad:?/i.test(el.textContent)) {
          el.textContent = this.t('quantity');
        }
      });

      // Fix availability text
      const availabilityTexts = document.querySelectorAll('.mm-product-info__availability-text');
      availabilityTexts.forEach((el) => {
        const text = el.textContent.trim();
        if (/available now/i.test(text) || /disponible ahora/i.test(text)) {
          el.textContent = this.t('availableNow');
        } else if (/currently unavailable/i.test(text) || /actualmente no disponible/i.test(text)) {
          el.textContent = this.t('currentlyUnavailable');
        } else if (/(\d+)\s*(in stock|en stock)/i.test(text)) {
          const match = text.match(/(\d+)\s*(in stock|en stock)/i);
          if (match) {
            el.textContent = `${match[1]} ${this.t('inStock')}`;
          }
        }
      });
    } finally {
      // Always reset the flag
      this.isApplyingTranslations = false;
    }
  }

  showMessage(message, type = 'info', options = {}) {
    // Create or update global message element
    let messageEl = document.querySelector('#mm-global-message');
    if (!messageEl) {
      messageEl = document.createElement('div');
      messageEl.id = 'mm-global-message';
      messageEl.className = 'mm-global-message';
      messageEl.setAttribute('role', 'alert');
      messageEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(messageEl);
    }

    // Clear previous content
    messageEl.innerHTML = '';

    // Add message text
    const messageText = document.createElement('span');
    messageText.className = 'mm-global-message__text';
    messageText.textContent = message;
    messageEl.appendChild(messageText);

    // Add action button if provided
    if (options.action && options.callback) {
      const actionButton = document.createElement('button');
      actionButton.className = 'mm-global-message__action';
      actionButton.textContent = options.action;
      actionButton.type = 'button';
      actionButton.addEventListener('click', () => {
        messageEl.classList.remove('mm-global-message--visible');
        options.callback();
      });
      messageEl.appendChild(actionButton);
    }

    messageEl.className = `mm-global-message mm-global-message--${type} mm-global-message--visible`;

    // Auto hide after 5 seconds (longer for error messages with actions)
    const hideDelay = (type === 'error' && options.action) ? 8000 : 3000;
    setTimeout(() => {
      messageEl.classList.remove('mm-global-message--visible');
    }, hideDelay);
  }

  dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      detail,
      bubbles: true,
      cancelable: true
    });

    (this.productForm || document).dispatchEvent(event);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new MinaRominaProduct());
} else {
  new MinaRominaProduct();
}

// Badge protection only - translations handled by Liquid
document.addEventListener('DOMContentLoaded', () => {
  // 🛡️ PROTECCIÓN: Respetar badge de descuento del nuevo snippet
  const mmBadge = document.querySelector('.mm-badge[data-mm-badge="true"]');
  if (mmBadge) {
    // Si algún script legacy le puso display:none inline, lo limpiamos
    mmBadge.style.removeProperty('display');
  }

  // Blindaje: si algún handler intenta ocultar badges genéricos, ignora los que tengan data-mm-badge
  try {
    if (typeof window.toggleDiscountBadge === 'function') {
      const prev = window.toggleDiscountBadge;
      window.toggleDiscountBadge = function(...args){
        const r = prev.apply(this, args);
        const b = document.querySelector('.mm-badge[data-mm-badge="true"]');
        if (b) b.style.removeProperty('display');
        return r;
      };
    }
  } catch(e) {
    // Silent fail
  }
});